import React from "react";
import { cn } from "@/lib/utils";
import { Heading } from "./Heading";

/**
 * SectionHeader — in-page section title block (lighter-weight than PageHeader).
 *
 * Use for section titles inside a page (`<h2>` level). PageHeader is
 * for the page's top-level `<h1>`.
 *
 * Composition:
 *   * title       — required
 *   * description — optional sub-text
 *   * actions     — right-aligned toolbar
 *
 * The container is a semantic <section> wrapper with the header row
 * separated by an optional divider (default: off — cleaner than
 * PageHeader which always renders one).
 */

export function SectionHeader({
  title,
  description,
  actions,
  divider = false,
  as = "h2",
  size = "xl",
  className,
  "data-testid": testId,
}) {
  return (
    <div
      data-testid={testId}
      className={cn(
        "flex items-start justify-between gap-4 mb-4",
        divider && "border-b border-[var(--ds-color-border)] pb-3",
        className,
      )}
    >
      <div className="min-w-0">
        {title && (
          <Heading as={as} size={size} weight="semibold" className="mt-0">
            {title}
          </Heading>
        )}
        {description && (
          <p className="mt-1 text-[13px] text-[var(--ds-color-ink-muted)] max-w-2xl">
            {description}
          </p>
        )}
      </div>
      {actions && <div className="shrink-0 flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export default SectionHeader;
